import React, { useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SendIcon from '@mui/icons-material/Send';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { BRAND, avatarColor, initialsOf } from '../../theme';
import { DriverInfo } from './types';
import { formatCurrency } from '../../utils/money';

export interface PayoutQueueGroup {
  driver: DriverInfo;
  invoices: any[];
  totalBeforeDeductions: number;
  percentageCutAmount: number;
  totalAfterDeductions: number;
}

interface PayoutQueueProps {
  groups: PayoutQueueGroup[];
  invoiceCount: number;
  queueTotalBefore: number;
  queueTotalAfter: number;
  /** Set when every driver in the queue has the same cut, otherwise null */
  uniformCut: number | null;
  submitting: boolean;
  onRemoveInvoice: (invoice: any) => void;
  onCollapse: () => void;
  onCancelAll: () => void;
  onSubmit: () => void;
}

const DriverGroup: React.FC<{
  group: PayoutQueueGroup;
  onRemoveInvoice: (invoice: any) => void;
}> = ({ group, onRemoveInvoice }) => {
  const [open, setOpen] = useState(true);
  const color = avatarColor(group.driver.dogtag || group.driver.name);

  return (
    <Paper variant="outlined" sx={{ mb: 1.5 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          px: 1.5,
          py: 1.25,
          cursor: 'pointer'
        }}
        onClick={() => setOpen(!open)}
      >
        <Avatar
          sx={{
            width: 30,
            height: 30,
            fontSize: 12,
            fontWeight: 700,
            bgcolor: `${color}22`,
            color
          }}
        >
          {initialsOf(group.driver.name)}
        </Avatar>
        <Typography variant="subtitle2" sx={{ flex: 1, minWidth: 0 }} noWrap>
          {group.driver.name}
        </Typography>
        <Chip
          size="small"
          label={`${group.invoices.length} invoice${group.invoices.length === 1 ? '' : 's'}`}
          sx={{ backgroundColor: '#EFF4FE', color: BRAND.blue }}
        />
        <IconButton size="small">{open ? <ExpandLessIcon /> : <ExpandMoreIcon />}</IconButton>
      </Box>

      <Collapse in={open}>
        <Divider />
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ pl: 1.5 }}>Invoice #</TableCell>
              <TableCell>Destination</TableCell>
              <TableCell align="right">Total Charges</TableCell>
              <TableCell padding="checkbox" />
            </TableRow>
          </TableHead>
          <TableBody>
            {group.invoices.map((invoice) => (
              <TableRow key={invoice.id}>
                <TableCell sx={{ pl: 1.5, color: 'primary.main', fontWeight: 600 }}>
                  {invoice.invoiceNumber || 'N/A'}
                </TableCell>
                <TableCell sx={{ maxWidth: 150, whiteSpace: 'normal' }}>
                  {invoice.destination || 'N/A'}
                </TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                  {formatCurrency(invoice.totalCharges || 0)}
                </TableCell>
                <TableCell padding="checkbox">
                  <Tooltip title="Remove from queue">
                    <IconButton size="small" onClick={() => onRemoveInvoice(invoice)}>
                      <CloseIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <Divider />
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            px: 1.5,
            py: 1.25,
            backgroundColor: '#F8FAFD'
          }}
        >
          <Typography variant="body2" fontWeight={600}>
            Driver Subtotal ({initialsOf(group.driver.name)})
          </Typography>
          <Typography variant="body2" fontWeight={700}>
            {formatCurrency(group.totalBeforeDeductions)}
          </Typography>
        </Box>
      </Collapse>
    </Paper>
  );
};

const PayoutQueue: React.FC<PayoutQueueProps> = ({
  groups,
  invoiceCount,
  queueTotalBefore,
  queueTotalAfter,
  uniformCut,
  submitting,
  onRemoveInvoice,
  onCollapse,
  onCancelAll,
  onSubmit
}) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: 2,
        py: 1.75,
        flexShrink: 0
      }}
    >
      <Typography variant="h6" sx={{ flex: 1 }}>
        Payout Queue
      </Typography>
      <Chip
        size="small"
        label={`${invoiceCount} invoice${invoiceCount === 1 ? '' : 's'}`}
        sx={{ backgroundColor: '#EFF4FE', color: BRAND.blue }}
      />
      <Tooltip title="Hide queue">
        <IconButton size="small" onClick={onCollapse}>
          <ChevronRightIcon />
        </IconButton>
      </Tooltip>
      <Tooltip title="Clear queue">
        <IconButton size="small" onClick={onCancelAll}>
          <CloseIcon />
        </IconButton>
      </Tooltip>
    </Box>
    <Divider />

    {/* The driver list is the only part that scrolls; the totals and the submit
        buttons stay put no matter how many loads are queued. */}
    <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: 2, py: 2 }}>
      <Alert severity="info" icon={false} sx={{ mb: 2 }}>
        Invoices in this queue will be paid to drivers. Review and submit when ready.
      </Alert>

      {groups.map((group) => (
        <DriverGroup key={group.driver.dogtag} group={group} onRemoveInvoice={onRemoveInvoice} />
      ))}
    </Box>

    <Divider />
    <Box sx={{ px: 2, py: 2, flexShrink: 0 }}>
      <Paper variant="outlined" sx={{ p: 1.5, mb: 2, backgroundColor: '#F8FAFD' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
          <Typography variant="body2" fontWeight={700}>
            Queue Total ({invoiceCount} invoice{invoiceCount === 1 ? '' : 's'})
          </Typography>
          <Typography variant="body2" fontWeight={700}>
            {formatCurrency(queueTotalBefore)}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="body2" color="text.secondary">
            Total Before Deductions
          </Typography>
          <Typography variant="body2">{formatCurrency(queueTotalBefore)}</Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="body2" color="text.secondary">
            {uniformCut === null
              ? 'Total After % Cut (varies by driver)'
              : `Total After % Cut (${uniformCut}%)`}
          </Typography>
          <Typography variant="body2" fontWeight={600}>
            {formatCurrency(queueTotalAfter)}
          </Typography>
        </Box>
      </Paper>

      <Button
        fullWidth
        variant="contained"
        size="large"
        endIcon={submitting ? undefined : <SendIcon />}
        disabled={submitting || invoiceCount === 0}
        onClick={onSubmit}
        sx={{ mb: 1.5, textTransform: 'uppercase', letterSpacing: 0.5 }}
      >
        {submitting ? <CircularProgress size={24} color="inherit" /> : 'Submit all to drivers'}
      </Button>
      <Button
        fullWidth
        variant="outlined"
        color="error"
        size="large"
        endIcon={<DeleteOutlineIcon />}
        disabled={submitting}
        onClick={onCancelAll}
        sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}
      >
        Cancel all
      </Button>
    </Box>
  </Box>
);

export default PayoutQueue;
